import { urls } from '~/preloaded';
import { GAME_TRANSACTION_STATUS } from '~/api/const';
import { ATTRIBUTES_VALIDATION_STATE, SOCIAL_NETWORKS_LINKS_FORMATS } from '~/constants';
import { post } from '~/helpers/api';
import { t } from '~/helpers/localization';
import { isPreModerateDescription } from '~/helpers/moderation';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';
import { promiseWithGlobalSpinner } from '~/store/utils/globalSpinner';

import {
  sendClanEditDescriptionCompleteNotification,
  sendClanEditDescriptionErrorNotification,
  sendClanEditDescriptionPreModerationNotification,
} from '~/web2ClientAPI/notifications';
import { actionsClanProfile } from '~/Actions/ActionClanProfile';
import { actionsClanRename } from '~/Actions/ActionClanRename';

import type { InferActionsType } from '~/Reducers';
import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { IClanEditStateFields } from '~/Reducers/ReducerClanEdit';
import type { IClanDisplay } from '~/Reducers/ReducerSupply';
import type { ROLE_NAMES } from '~/roles';
import type { IAppDispatch, AppAsyncThunk } from '~/store';
import type { IApiChangeClanAttributes, IApiError } from '~/types/api';
import type { IClanCommunityUrls } from '~/types/declaration';

export const START_CLAN_EDIT = 'START_CLAN_EDIT';
export const UPDATE_CLAN_EDIT_STATE = 'UPDATE_CLAN_EDIT_STATE';
export const UPDATE_CLAN_EDIT_ERROR = 'UPDATE_CLAN_EDIT_ERROR';

export type ActionsType = InferActionsType<typeof actionsClanEdit>;

type ClanEditStateType = {
  savedDescription: string;
  isValidationError: boolean;
  error: IApiError;
};

type ClanEditErrorType = {
  error?: string;
  errors?: unknown;
  current?: unknown;
  system_error?: boolean;
  socials_error?: IClanCommunityUrls;
} | null;

export const actionsClanEdit = {
  startClanEdit: (field: keyof IClanEditStateFields) =>
    ({
      type: START_CLAN_EDIT,
      field,
    }) as const,

  updateClanEditState: (field: keyof IClanEditStateFields, data: ClanEditStateType | null) =>
    ({
      type: UPDATE_CLAN_EDIT_STATE,
      field,
      data,
    }) as const,

  updateClanEditError: (field: keyof IClanEditStateFields, data: ClanEditErrorType | null) =>
    ({
      type: UPDATE_CLAN_EDIT_ERROR,
      field,
      data,
    }) as const,
};

const processExcludedFromClanError = (
  dispatch: IAppDispatch,
  sendErrorNotification: typeof sendClanEditDescriptionErrorNotification,
  clan?: IClanDisplay,
) => {
  sendErrorNotification('excluded_from_clan', clan);

  const promise = dispatch(syncAccountInfoThunk());
  void promiseWithGlobalSpinner(dispatch, promise);
};

const processError = (
  dispatch: IAppDispatch,
  error: {
    additional_info?: {
      reason?: string;
      role?: ROLE_NAMES;
      field?: keyof IClanCreateStateFields;
      clan?: IClanDisplay;
    };
    error?: string;
    error_code?: string;
  },
  sendErrorNotification: typeof sendClanEditDescriptionErrorNotification,
) => {
  const reason = error?.additional_info?.reason;

  if (reason === 'insufficient_permissions') {
    const role = error?.additional_info?.role ?? null;
    if (role) {
      dispatch(currentAccountActions.updateAccountInfo({ roleName: role }));
      sendErrorNotification(reason);
    } else {
      processExcludedFromClanError(dispatch, sendErrorNotification, error?.additional_info?.clan);
    }
  } else if (reason === 'excluded_from_clan') {
    processExcludedFromClanError(dispatch, sendErrorNotification, error?.additional_info?.clan);
  } else if (reason === 'clan_already_exist') {
    sendErrorNotification('clan_already_exist');
    // TODO WGCP-3104 add to response reason not_enought_gold
  } else if (error?.error === 'NO_GOLD') {
    sendErrorNotification('no_gold');
  } else if (reason === 'rename_in_cooldown') {
    sendErrorNotification(reason);
  } else if (error?.error_code === 'restriction_error') {
    // TODO WGCP-3104 add to response reason restriction_error or something similar
    sendErrorNotification('restriction_error', '');
  } else if (reason === 'err_bad_words') {
    const messages = {
      name: t('Название содержит запрещенные слова.'),
      tag: t('Тег содержит запрещенные слова.'),
    };

    const field = error?.additional_info?.field;
    if (field) {
      dispatch(
        actionsClanRename.finishCheckAvailabilityRenamingClan({
          field: field,
          error: messages[field],
          available: ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE,
        }),
      );
    }
  } else {
    sendErrorNotification();
  }
};

export const shouldHideDialogOnError = (error: { additional_info?: { reason?: string } }) => {
  if (!error?.additional_info?.reason) {
    return false;
  }

  return ['insufficient_permissions', 'excluded_from_clan'].includes(error.additional_info.reason);
};

export const changeClanAttributes =
  (
    payload: {
      description?: string;
      rawDescription?: string;
      communityUrls?: IClanCommunityUrls;
    },
    disableSuccessfulNotification = false,
    signal?: AbortSignal,
  ): AppAsyncThunk<boolean> =>
  async (dispatch, getState) => {
    const state = getState();
    const clanId = state.currentAccount.clanId;

    dispatch(actionsClanEdit.startClanEdit('clanDescription'));

    try {
      const url = urls.changeAttributes;

      const response = await post<IApiChangeClanAttributes>(url, payload, signal && { signal });

      if (response.status === GAME_TRANSACTION_STATUS.ERROR || response.errors || !!response.error) {
        processError(dispatch, response, sendClanEditDescriptionErrorNotification);
        dispatch(actionsClanEdit.updateClanEditError('clanDescription', response));

        return shouldHideDialogOnError(response);
      }

      if (!disableSuccessfulNotification) {
        if (isPreModerateDescription) {
          sendClanEditDescriptionPreModerationNotification();
        } else {
          sendClanEditDescriptionCompleteNotification();
        }
      }

      dispatch(actionsClanEdit.updateClanEditError('clanDescription', null));

      if (payload.description) {
        payload.rawDescription = payload.description;
        delete payload.description;
      }

      if (clanId) {
        dispatch(actionsClanEdit.updateClanEditState('clanDescription', null));
        dispatch(actionsClanProfile.changeClanAttributesSuccessed({ ...payload, clanId }));
      }

      return true;
    } catch (err: unknown) {
      type ErrorWithResponse = {
        response?: {
          body?: {
            description?: string;
          };
        };
      };
      const error = err as ErrorWithResponse;

      sendClanEditDescriptionErrorNotification();

      let socialsError: IClanCommunityUrls | undefined = undefined;
      const socialsErrorPrefix = 'Community urls are invalid:';
      const errorDescription: string | undefined = error?.response?.body?.description;

      if (typeof errorDescription === 'string' && errorDescription.indexOf(socialsErrorPrefix) >= 0) {
        const regexPattern = `${socialsErrorPrefix}\\s*(.+)`;
        const regex = new RegExp(regexPattern);
        const match = errorDescription.match(regex);

        if (match && match[1]) {
          socialsError = match[1].split(',').reduce<IClanCommunityUrls>((acc, social) => {
            return {
              ...acc,
              [social]: t('Correct link format must be: %(linkFormat)s', {
                linkFormat: SOCIAL_NETWORKS_LINKS_FORMATS[social as keyof IClanCommunityUrls],
              }),
            };
          }, {} as IClanCommunityUrls);
        }
      }

      dispatch(
        actionsClanEdit.updateClanEditError('clanDescription', {
          error: t('Произошла ошибка. Повторите попытку позже'),
          socials_error: socialsError,
        }),
      );

      return false;
    }
  };
